import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Tooltip,
} from '@mui/material';
import api from '../api';
import { formatFen } from '../utils/money';
import { showSuccess, showError, showWarning } from '../components/GlobalSnackbar';

// 工资统计（v2.2 起含支付跟踪：已付/欠款来自 /api/reports/wage-balance，按工人合并到月表）
export default function SalaryReport() {
  const [salaryData, setSalaryData] = useState([]);
  const [balances, setBalances] = useState({ workers: [], totals: { totalPayable: 0, totalPaid: 0, balance: 0 } });
  const [loading, setLoading] = useState(true);
  // 财务页底线：加载失败必须显式报错+重试，渲染成"0 元工资表"比报错危险得多
  const [loadError, setLoadError] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));

  // 记支付弹窗
  const [payDialog, setPayDialog] = useState(null); // { workerId, workerName }
  const [payForm, setPayForm] = useState({ amountYuan: '', date: new Date().toISOString().slice(0, 10), method: '现金', notes: '' });
  const [paySaving, setPaySaving] = useState(false);
  // 支付流水（弹窗内展示+删除错账）
  const [payHistory, setPayHistory] = useState([]);
  const [confirmDeletePay, setConfirmDeletePay] = useState(null); // { id, amount, date }

  useEffect(() => {
    fetchData();
  }, [month]); // eslint-disable-line

  const fetchData = async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const [salaryRes, balanceRes] = await Promise.all([
        api.get(`/api/reports/salary?month=${month}`),
        api.get('/api/reports/wage-balance'),
      ]);
      setSalaryData(salaryRes.data);
      setBalances(balanceRes.data);
    } catch (error) {
      console.error('Error fetching salary data:', error);
      setLoadError(error.response?.data?.error || '工资数据加载失败，请检查网络后重试');
    } finally {
      setLoading(false);
    }
  };

  const balanceOf = useCallback(
    (workerId) => balances.workers.find(w => w.workerId === workerId) || { totalPaid: 0, balance: 0 },
    [balances]
  );

  const openPayDialog = async (workerId, workerName) => {
    setPayForm({ amountYuan: '', date: new Date().toISOString().slice(0, 10), method: '现金', notes: '' });
    setPayDialog({ workerId, workerName });
    setPayHistory([]);
    try {
      const hist = await api.get(`/api/wage-payments?workerId=${workerId}`);
      setPayHistory(hist.data || []);
    } catch (_) { /* 流水加载失败不阻塞记账 */ }
  };

  const deletePayment = async (id) => {
    try {
      await api.delete(`/api/wage-payments/${id}`);
      setConfirmDeletePay(null);
      showSuccess('已删除该笔支付');
      if (payDialog) {
        const hist = await api.get(`/api/wage-payments?workerId=${payDialog.workerId}`).catch(() => null);
        if (hist) setPayHistory(hist.data || []);
      }
      fetchData();
    } catch (error) {
      showError(error.response?.data?.error || '删除失败，请重试');
    }
  };

  const submitPayment = async () => {
    const amountFen = Math.round(parseFloat(payForm.amountYuan || '0') * 100);
    if (!amountFen || amountFen <= 0) { showWarning('请输入大于 0 的金额'); return; }
    try {
      setPaySaving(true);
      await api.post('/api/wage-payments', {
        workerId: payDialog.workerId,
        amount: amountFen,
        date: payForm.date,
        method: payForm.method,
        notes: payForm.notes || undefined,
      });
      setPayDialog(null);
      showSuccess('已记录支付');
      fetchData();
    } catch (error) {
      showError(error.response?.data?.error || '保存失败，请重试');
    } finally {
      setPaySaving(false);
    }
  };

  const totals = salaryData.reduce((acc, item) => ({
    workDays: acc.workDays + item.workDays,
    workAmount: acc.workAmount + item.workAmount,
    overtimeAmount: acc.overtimeAmount + item.overtimeAmount,
    mealAllowanceTotal: acc.mealAllowanceTotal + item.mealAllowanceTotal,
    advanceAmount: acc.advanceAmount + item.advanceAmount,
    deductionAmount: acc.deductionAmount + item.deductionAmount,
    subsidyAmount: acc.subsidyAmount + item.subsidyAmount,
    totalAmount: acc.totalAmount + item.totalAmount,
  }), {
    workDays: 0,
    workAmount: 0,
    overtimeAmount: 0,
    mealAllowanceTotal: 0,
    advanceAmount: 0,
    deductionAmount: 0,
    subsidyAmount: 0,
    totalAmount: 0,
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="400px" gap={2}>
        <Typography color="error">{loadError}</Typography>
        <Button variant="contained" onClick={fetchData}>重试</Button>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">工资统计</Typography>
        <TextField
          type="month"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          variant="outlined"
          size="small"
        />
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>总出勤</Typography>
              <Typography variant="h5">{totals.workDays.toFixed(1)} 天</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>应发工资（本月）</Typography>
              <Typography variant="h5" color="primary">{formatFen(totals.totalAmount)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>累计已付工资（全期）</Typography>
              <Typography variant="h5">{formatFen(balances.totals.totalPaid)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: balances.totals.balance > 0 ? '1px solid' : 'none', borderColor: 'warning.main' }}>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>累计欠付工资（全期）</Typography>
              <Typography variant="h5" color={balances.totals.balance > 0 ? 'warning.dark' : 'success.main'}>
                {formatFen(balances.totals.balance)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {salaryData.length === 0 ? (
        <Card>
          <CardContent>
            <Typography variant="h6" align="center" color="text.secondary">
              暂无工资数据
            </Typography>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>工人姓名</TableCell>
                <TableCell align="right">出勤天数</TableCell>
                <TableCell align="right">记工工资</TableCell>
                <TableCell align="right">加班工资</TableCell>
                <TableCell align="right">饭补</TableCell>
                <TableCell align="right">补贴</TableCell>
                <TableCell align="right">借支</TableCell>
                <TableCell align="right">扣款</TableCell>
                <TableCell align="right">应发工资</TableCell>
                <TableCell align="right">累计已付</TableCell>
                <TableCell align="right">欠款余额</TableCell>
                <TableCell align="center">操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {salaryData.map((item) => {
                const bal = balanceOf(item.workerId);
                return (
                  <TableRow key={item.workerId}>
                    <TableCell>{item.workerName}</TableCell>
                    <TableCell align="right">{item.workDays.toFixed(1)}</TableCell>
                    <TableCell align="right">{formatFen(item.workAmount)}</TableCell>
                    <TableCell align="right">{formatFen(item.overtimeAmount)}</TableCell>
                    <TableCell align="right">{formatFen(item.mealAllowanceTotal)}</TableCell>
                    <TableCell align="right">{formatFen(item.subsidyAmount)}</TableCell>
                    <TableCell align="right">{formatFen(item.advanceAmount)}</TableCell>
                    <TableCell align="right">{formatFen(item.deductionAmount)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(item.totalAmount)}</TableCell>
                    <TableCell align="right">{formatFen(bal.totalPaid)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: bal.balance > 0 ? 'bold' : 'normal', color: bal.balance > 0 ? 'warning.dark' : 'success.main' }}>
                      {formatFen(bal.balance)}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="记录给这位工人的现金/转账支付">
                        <Button size="small" variant="outlined" onClick={() => openPayDialog(item.workerId, item.workerName)}>
                          记支付
                        </Button>
                      </Tooltip>
                      <Tooltip title="打开可打印的大字号对账单">
                        <Button size="small" variant="text" onClick={() => window.open(`/workers/${item.workerId}/statement/${month}`, '_blank')}>
                          对账单
                        </Button>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: 'grey.100' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>合计</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totals.workDays.toFixed(1)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.workAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.overtimeAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.mealAllowanceTotal)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.subsidyAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.advanceAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatFen(totals.deductionAmount)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: 'primary.main' }}>{formatFen(totals.totalAmount)}</TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 记支付弹窗 */}
      <Dialog open={!!payDialog} onClose={() => setPayDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>记支付 · {payDialog?.workerName}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              当前欠款：{formatFen(payDialog ? balanceOf(payDialog.workerId).balance : 0)}
            </Typography>
            <TextField
              label="支付金额（元）"
              value={payForm.amountYuan}
              onChange={(e) => setPayForm({ ...payForm, amountYuan: e.target.value })}
              type="number"
              inputProps={{ min: '0', step: '0.01' }}
              autoFocus
              fullWidth
            />
            <TextField
              label="支付日期"
              type="date"
              value={payForm.date}
              onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
              fullWidth
            />
            <TextField
              label="支付方式"
              select
              value={payForm.method}
              onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
              fullWidth
            >
              {['现金', '微信', '支付宝', '银行转账'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField
              label="备注（选填）"
              value={payForm.notes}
              onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
              fullWidth
            />
            {payHistory.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  支付记录（点垃圾桶删除记错的账）
                </Typography>
                <Box sx={{ maxHeight: 180, overflowY: 'auto' }}>
                  {payHistory.map((p) => (
                    <Box key={p.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5, borderBottom: '1px dashed', borderColor: 'divider' }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {p.date} · {p.method || '未记方式'} {p.notes ? `· ${p.notes}` : ''}
                      </Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatFen(p.amount)}
                      </Typography>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => setConfirmDeletePay({ id: p.id, amount: p.amount, date: p.date })}
                      >
                        删除
                      </Button>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPayDialog(null)}>取消</Button>
          <Button onClick={submitPayment} variant="contained" disabled={paySaving}>
            {paySaving ? '保存中…' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 删除支付确认 */}
      <Dialog open={!!confirmDeletePay} onClose={() => setConfirmDeletePay(null)} maxWidth="xs">
        <DialogTitle>删除支付记录？</DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDeletePay?.date} 的 {formatFen(confirmDeletePay?.amount || 0)} 将被删除，欠款会相应加回。此操作不可撤销。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeletePay(null)}>取消</Button>
          <Button color="error" variant="contained" onClick={() => deletePayment(confirmDeletePay.id)}>
            确认删除
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
